import { useCallback, useState } from 'react';
import { format } from 'date-fns';
import { postingLogApi } from '@/services/postingLogApi';
import { toast } from 'sonner';
import { downloadCsv } from '@/utils/reportCsv';
import type { PostingLogQuery } from '@/utils/PostingLog/types';
import { mapPostingLogRow } from '@/utils/PostingLog/mappers';
import { buildPostingLogCsv } from '@/utils/PostingLog/csv';

interface UsePostingLogExportParams {
  allowExport: boolean;
  matchesNothing: boolean;
  query: PostingLogQuery;
}

export function usePostingLogExport({ allowExport, matchesNothing, query }: UsePostingLogExportParams) {
  const [isExporting, setIsExporting] = useState(false);

  // Export — every line matching the current filters, not just the visible page
  const handleExport = useCallback(async () => {
    if (!allowExport) return;
    setIsExporting(true);
    try {
      const exportRows = matchesNothing
        ? []
        : (await postingLogApi.getAllPostingLogRows(query)).map(mapPostingLogRow);
      downloadCsv(buildPostingLogCsv(exportRows), `posting-log-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      toast.success('Export successful', {
        description: `Exported ${exportRows.length} lines to CSV.`,
      });
    } catch {
      toast.error('Export failed', { description: 'There was an error generating the CSV.' });
    } finally {
      setIsExporting(false);
    }
  }, [allowExport, matchesNothing, query]);

  return { isExporting, handleExport };
}
